import { isPosthogEnabled } from "config";
import { getPosthogIfReady } from "config/posthog-runtime";
import { getAcquisitionAttributionForEvent } from "utils/readAcquisitionAttribution";

/** Header the backend reads on email sign-up and on the social sign-in start request. */
export const SIGNUP_ATTRIBUTION_HEADER = "x-signup-attribution";

const MAX_VALUE_LENGTH = 256;
const MAX_HEADER_LENGTH = 2048;

/**
 * The only keys the backend accepts. Listed from most to least important: when the encoded
 * header is too long, keys are dropped from the end of this list first.
 */
export const SIGNUP_ATTRIBUTION_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "posthog_distinct_id",
  "utm_term",
  "utm_content",
  "landing_path",
  "referrer",
] as const;

export type SignupAttributionKey = (typeof SIGNUP_ATTRIBUTION_KEYS)[number];
export type SignupAttribution = Partial<Record<SignupAttributionKey, string>>;

const clampValue = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return Array.from(trimmed).slice(0, MAX_VALUE_LENGTH).join("");
};

/** Drops the query and hash so tokens in a referring URL never reach our database. */
const stripQuery = (url: string): string | undefined => {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return undefined;
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return undefined;
  }
};

const toBase64Url = (text: string): string => {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

/**
 * Encodes attribution as base64url(JSON) with only the allowed keys, each value at most 256
 * characters, and the whole header at most 2048 characters. Returns null when nothing is left.
 */
export const encodeSignupAttribution = (attribution: SignupAttribution): string | null => {
  const kept: SignupAttribution = {};
  for (const key of SIGNUP_ATTRIBUTION_KEYS) {
    const value = clampValue(attribution[key]);
    if (value) kept[key] = value;
  }

  const keys = SIGNUP_ATTRIBUTION_KEYS.filter((key) => key in kept);
  while (keys.length > 0) {
    const payload: SignupAttribution = {};
    for (const key of keys) payload[key] = kept[key];
    const encoded = toBase64Url(JSON.stringify(payload));
    if (encoded.length <= MAX_HEADER_LENGTH) return encoded;
    keys.pop();
  }
  return null;
};

type PosthogInitialPersonInfo = { r?: unknown; u?: unknown };

const readPosthogContext = (): {
  distinctId?: string;
  initialUrl?: string;
  initialReferrer?: string;
} => {
  if (!isPosthogEnabled) return {};
  const posthog = getPosthogIfReady();
  if (!posthog) return {};
  try {
    const initial = posthog.get_property("$initial_person_info") as
      | PosthogInitialPersonInfo
      | undefined;
    return {
      distinctId: posthog.get_distinct_id?.() || undefined,
      initialUrl: typeof initial?.u === "string" ? initial.u : undefined,
      initialReferrer: typeof initial?.r === "string" ? initial.r : undefined,
    };
  } catch {
    return {};
  }
};

const readLandingPath = (initialUrl: string | undefined): string | undefined => {
  if (initialUrl) {
    try {
      return new URL(initialUrl).pathname;
    } catch {
      /* fall through to the current page */
    }
  }
  return typeof window !== "undefined" ? window.location.pathname : undefined;
};

const readReferrer = (
  initialReferrer: string | undefined,
  referringDomain: string | undefined
): string | undefined => {
  const fromPosthog = initialReferrer && initialReferrer !== "$direct" ? initialReferrer : "";
  const fromDocument =
    typeof document !== "undefined" && document.referrer ? document.referrer : "";
  for (const candidate of [fromPosthog, fromDocument]) {
    if (!candidate) continue;
    const stripped = stripQuery(candidate);
    if (!stripped) continue;
    if (typeof window !== "undefined" && stripped.startsWith(window.location.origin)) continue;
    return stripped;
  }
  return referringDomain;
};

/**
 * First-touch attribution for a new account: UTMs and ad click ids from the sign-up URL, the
 * session's stored landing values, or PostHog, plus the landing path, the referrer, and the
 * PostHog anonymous id when PostHog is loaded.
 */
export const collectSignupAttribution = (): SignupAttribution => {
  const acquisition = getAcquisitionAttributionForEvent();
  const posthog = readPosthogContext();
  return {
    utm_source: acquisition.utm_source,
    utm_medium: acquisition.utm_medium,
    utm_campaign: acquisition.utm_campaign,
    utm_term: acquisition.utm_term,
    utm_content: acquisition.utm_content,
    gclid: acquisition.gclid,
    gbraid: acquisition.gbraid,
    wbraid: acquisition.wbraid,
    landing_path: readLandingPath(posthog.initialUrl),
    referrer: readReferrer(posthog.initialReferrer, acquisition.referring_domain),
    posthog_distinct_id: posthog.distinctId,
  };
};

/** `{ "x-signup-attribution": value }`, or an empty object when there is nothing to send. */
export const buildSignupAttributionHeaders = (): Record<string, string> => {
  try {
    const encoded = encodeSignupAttribution(collectSignupAttribution());
    return encoded ? { [SIGNUP_ATTRIBUTION_HEADER]: encoded } : {};
  } catch {
    return {};
  }
};
